import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import axios from 'axios';
import { useAuth } from '../App';
import { API } from '../utils';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ArrowLeft, CheckCircle, Calendar as CalendarIcon, DollarSign, Edit, AlertCircle } from 'lucide-react';

const Agreement = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [booking, setBooking] = useState(null);
  const [agreement, setAgreement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [signing, setSigning] = useState(false);
  const [originalValues, setOriginalValues] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  const [formData, setFormData] = useState({
    scope: '',
    deliverables_description: '',
    revisions_included: 2,
    additional_revision_rate: 0,
    deadline: null,
    terms: 'Standard terms and conditions apply. Payment will be held in escrow until deliverables are approved.'
  });

  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const config = { withCredentials: true, headers };

  useEffect(() => { fetchBooking(); }, [bookingId]);

  useEffect(() => {
    if (originalValues) {
      const scopeChanged = formData.scope && originalValues.original_scope && formData.scope !== originalValues.original_scope;
      const deliverablesChanged = formData.deliverables_description && originalValues.original_deliverables && formData.deliverables_description !== originalValues.original_deliverables;
      const deadlineChanged = formData.deadline && originalValues.original_deadline && format(formData.deadline, 'yyyy-MM-dd') !== originalValues.original_deadline;
      setHasChanges(scopeChanged || deliverablesChanged || deadlineChanged);
    }
  }, [formData, originalValues]);

  const fetchBooking = async () => {
    try {
      const response = await axios.get(`${API}/bookings/${bookingId}`, config);
      setBooking(response.data);
      if (response.data.agreement) {
        setAgreement(response.data.agreement);
      } else {
        await fetchOriginalValues();
      }
    } catch (error) {
      toast.error('Booking not found');
      navigate('/bookings');
    } finally {
      setLoading(false);
    }
  };

  const fetchOriginalValues = async () => {
    try {
      const response = await axios.get(`${API}/bookings/${bookingId}/original-values`, config);
      if (response.data.has_original_values) {
        setOriginalValues(response.data);
        setFormData(prev => ({
          ...prev,
          scope: response.data.original_scope || '',
          deliverables_description: response.data.original_deliverables || '',
          revisions_included: response.data.proposed_revisions || 2,
          deadline: response.data.original_deadline ? new Date(response.data.original_deadline) : null
        }));
      }
    } catch (error) {
      console.log('No original values available');
    }
  };

  const handleCreateAgreement = async () => {
    setCreating(true);
    try {
      await axios.post(`${API}/agreements`, {
        booking_id: bookingId,
        scope: booking?.project_description || booking?.project_title || '',
        deliverables_description: booking?.deliverables || '',
        revisions_included: formData.revisions_included,
        additional_revision_rate: formData.additional_revision_rate,
        deadline: booking?.delivery_date || '',
        terms: formData.terms
      }, config);
      toast.success('Agreement created!');
      fetchBooking();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create agreement');
    } finally {
      setCreating(false);
    }
  };

  const handleSign = async () => {
    if (!agreement) return;
    setSigning(true);
    try {
      const response = await axios.post(`${API}/agreements/${agreement.agreement_id}/sign`, {}, config);
      toast.success('Agreement acknowledged!');
      if (response.data.ready_for_payment) toast.info('Both parties have acknowledged. Ready for payment!');
      fetchBooking();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to acknowledge agreement');
    } finally {
      setSigning(false);
    }
  };

  const handlePayment = async () => {
    try {
      const response = await axios.post(`${API}/payments/checkout`, { booking_id: bookingId, origin_url: window.location.origin }, config);
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payment failed');
    }
  };

  const hasUserSigned = () => {
    if (!agreement || !user) return false;
    if (user.user_id === agreement.business_id) return agreement.business_signed;
    if (user.user_id === agreement.creative_id) return agreement.creative_signed;
    return false;
  };

  const isBothSigned = () => agreement?.business_signed && agreement?.creative_signed;

  const statusColors = {
    active: 'bg-[#f0fdf4] text-[#166534] border border-[#bbf7d0]',
    completed: 'bg-[#eff6ff] text-[#1e40af] border border-[#bfdbfe]',
    draft: 'bg-[#f5ead0] text-[#8a6a1a] border border-[#e8d49a]',
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f7f5f2]">
        <div className="animate-pulse text-[#a0a0a0] text-sm">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7f5f2]">
      {/* Header */}
      <div className="bg-white border-b border-[#e4e1db]">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/bookings" className="text-[#a0a0a0] hover:text-[#0e0e0e] transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <p className="text-[10px] font-bold tracking-[1.8px] uppercase text-[#a0a0a0]">Contract</p>
              <h1 className="font-display font-normal text-[22px] text-[#0e0e0e] tracking-[-0.3px] leading-tight">Project Agreement</h1>
            </div>
          </div>
          <Link to="/dashboard" className="font-display font-normal text-[18px] text-[#0e0e0e] tracking-tight">
            FrameFinder
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-6 py-8">

        {/* ── CREATE FORM ── */}
        {!agreement ? (
          <div className="bg-white rounded-[20px] border border-[#e4e1db] p-7">
            <h2 className="font-display font-normal text-[20px] text-[#0e0e0e] mb-6">Create Agreement</h2>

            <div className="space-y-6">
              {/* Read-only booking details */}
              <div className="space-y-4 p-5 bg-[#f7f5f2] rounded-[16px] border border-[#e4e1db]">
                <p className="text-[10px] font-bold tracking-[1.8px] uppercase text-[#a0a0a0]">Booking Details</p>

                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-widest text-[#a0a0a0] mb-1">Project Scope</p>
                  <p className="text-[14px] text-[#0e0e0e] bg-white border border-[#e4e1db] rounded-[10px] p-3 min-h-[52px]">
                    {booking?.project_description || booking?.project_title || '—'}
                  </p>
                </div>

                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-widest text-[#a0a0a0] mb-1">Expected Deliverables</p>
                  <p className="text-[14px] text-[#0e0e0e] bg-white border border-[#e4e1db] rounded-[10px] p-3 min-h-[44px]">
                    {booking?.deliverables || '—'}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-widest text-[#a0a0a0] mb-1">Shoot Date</p>
                    <p className="text-[14px] text-[#0e0e0e] bg-white border border-[#e4e1db] rounded-[10px] p-3">{booking?.date || '—'}</p>
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-widest text-[#a0a0a0] mb-1">Shoot Time</p>
                    <p className="text-[14px] text-[#0e0e0e] bg-white border border-[#e4e1db] rounded-[10px] p-3">{booking?.time || '—'}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-widest text-[#a0a0a0] mb-1">Delivery Date</p>
                    <p className="text-[14px] text-[#0e0e0e] bg-white border border-[#e4e1db] rounded-[10px] p-3">{booking?.delivery_date || '—'}</p>
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-widest text-[#a0a0a0] mb-1">Location</p>
                    <p className="text-[14px] text-[#0e0e0e] bg-white border border-[#e4e1db] rounded-[10px] p-3">{booking?.location || '—'}</p>
                  </div>
                </div>

                {booking?.notes && (
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-widest text-[#a0a0a0] mb-1">Notes from Client</p>
                    <p className="text-[14px] text-[#0e0e0e] bg-white border border-[#e4e1db] rounded-[10px] p-3">{booking.notes}</p>
                  </div>
                )}
              </div>

              {/* Editable fields - only photographer can set revisions */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-widest text-[#a0a0a0] mb-1.5">Revisions Included</p>
                  {user?.role === 'creative' ? (
                    <Input
                      type="number" min="0" max="10"
                      value={formData.revisions_included}
                      onChange={(e) => setFormData({ ...formData, revisions_included: parseInt(e.target.value) })}
                      className="border-[#e4e1db] focus:border-[#0e0e0e] rounded-[10px]"
                      data-testid="revisions-input"
                    />
                  ) : (
                    <p className="text-[14px] text-[#0e0e0e] bg-white border border-[#e4e1db] rounded-[10px] p-3">{formData.revisions_included}</p>
                  )}
                </div>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-widest text-[#a0a0a0] mb-1.5">Extra Revision Rate ($)</p>
                  {user?.role === 'creative' ? (
                    <Input
                      type="number" min="0" step="5"
                      value={formData.additional_revision_rate}
                      onChange={(e) => setFormData({ ...formData, additional_revision_rate: parseFloat(e.target.value) || 0 })}
                      placeholder="0"
                      className="border-[#e4e1db] focus:border-[#0e0e0e] rounded-[10px]"
                      data-testid="additional-revision-rate-input"
                    />
                  ) : (
                    <p className="text-[14px] text-[#0e0e0e] bg-white border border-[#e4e1db] rounded-[10px] p-3">${formData.additional_revision_rate || 0}</p>
                  )}
                  <p className="text-[11px] text-[#a0a0a0] mt-1">Rate per revision after included ones</p>
                </div>
              </div>

              <button
                onClick={handleCreateAgreement}
                disabled={creating}
                data-testid="create-agreement-btn"
                className="w-full py-3 rounded-full bg-[#0e0e0e] text-white text-[14px] font-medium hover:opacity-85 transition-opacity disabled:opacity-50"
              >
                {creating ? 'Creating...' : 'Create Agreement'}
              </button>
            </div>
          </div>

        ) : (
          /* ── VIEW AGREEMENT ── */
          <div className="bg-white rounded-[20px] border border-[#e4e1db] p-7">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display font-normal text-[20px] text-[#0e0e0e]">Agreement Details</h2>
              <span className={`px-3 py-1 rounded-full text-[12px] font-medium ${statusColors[agreement.status] || statusColors.draft}`}>
                {agreement.status}
              </span>
            </div>

            {/* Changes notification for creatives */}
            {user?.role === 'creative' && agreement.has_changes_from_original && agreement.original_values && (
              <div className="p-4 bg-[#fffbeb] rounded-[12px] border border-[#fde68a] mb-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-[#d97706] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-[#92400e] text-[14px]">Terms Modified from Original Project</p>
                    <p className="text-[13px] text-[#b45309] mt-1">The client has made changes from the original project posting. Please review carefully before signing.</p>
                    <div className="mt-3 space-y-2 text-[13px]">
                      {agreement.original_values.scope && agreement.scope?.trim() !== agreement.original_values.scope?.trim() && (
                        <div className="p-2 bg-white rounded-[8px] border border-[#fde68a] space-y-1">
                          <p className="font-medium text-[#92400e]">Scope changed:</p>
                          <p className="text-[#a0a0a0] line-through text-[12px]">{agreement.original_values.scope}</p>
                          <p className="text-[#0e0e0e] text-[12px]">{agreement.scope}</p>
                        </div>
                      )}
                      {agreement.original_values.deliverables && agreement.deliverables_description?.trim() !== agreement.original_values.deliverables?.trim() && (
                        <div className="p-2 bg-white rounded-[8px] border border-[#fde68a] space-y-1">
                          <p className="font-medium text-[#92400e]">Deliverables changed:</p>
                          <p className="text-[#a0a0a0] line-through text-[12px]">{agreement.original_values.deliverables}</p>
                          <p className="text-[#0e0e0e] text-[12px]">{agreement.deliverables_description}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-5">
              <h3 className="font-semibold text-[16px] text-[#0e0e0e]">{booking?.project_title}</h3>

              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: 'Shoot Date', value: booking?.date || '—' },
                  { label: 'Shoot Time', value: booking?.time || '—' },
                  { label: 'Delivery Date', value: booking?.delivery_date || '—' },
                  { label: 'Location', value: booking?.location || '—' },
                ].map(({ label, value }) => (
                  <div key={label}>
                    <p className="text-[11px] font-semibold uppercase tracking-widest text-[#a0a0a0] mb-0.5">{label}</p>
                    <p className="text-[14px] text-[#0e0e0e]">{value}</p>
                  </div>
                ))}
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-widest text-[#a0a0a0] mb-0.5">Total Amount</p>
                  <p className="text-[18px] font-semibold text-[#0e0e0e]">${parseFloat(agreement.total_amount || 0).toFixed(2)}</p>
                </div>
              </div>

              <div className="border-t border-[#e4e1db] pt-5 space-y-4">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-widest text-[#a0a0a0] mb-1">Scope</p>
                  <p className="text-[14px] text-[#0e0e0e]">{agreement.scope}</p>
                </div>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-widest text-[#a0a0a0] mb-1">Deliverables</p>
                  <p className="text-[14px] text-[#0e0e0e]">{agreement.deliverables_description}</p>
                </div>
                {booking?.notes && (
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-widest text-[#a0a0a0] mb-1">Notes from Client</p>
                    <p className="text-[14px] text-[#0e0e0e]">{booking.notes}</p>
                  </div>
                )}
              </div>

              <div className="border-t border-[#e4e1db] pt-5 grid grid-cols-2 gap-4">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-widest text-[#a0a0a0] mb-0.5">Revisions Included</p>
                  <p className="text-[14px] font-semibold text-[#0e0e0e]">{agreement.revisions_included}</p>
                </div>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-widest text-[#a0a0a0] mb-0.5">Extra Revision Rate</p>
                  <p className="text-[14px] font-semibold text-[#0e0e0e]">${parseFloat(agreement.additional_revision_rate || 0).toFixed(2)}</p>
                </div>
              </div>

              <div className="border-t border-[#e4e1db] pt-5">
                <p className="text-[11px] font-semibold uppercase tracking-widest text-[#a0a0a0] mb-1">Terms</p>
                <p className="text-[13px] text-[#5a5a5a]">{agreement.terms}</p>
              </div>

              {/* Signatures */}
              <div className="bg-[#f7f5f2] rounded-[14px] border border-[#e4e1db] p-4">
                <p className="text-[10px] font-bold tracking-[1.8px] uppercase text-[#a0a0a0] mb-3">Acknowledgements</p>
                <div className="space-y-3">
                  {[
                    { label: 'Client', signed: agreement.business_signed, date: agreement.business_signed_at },
                    { label: 'Photographer', signed: agreement.creative_signed, date: agreement.creative_signed_at },
                  ].map(({ label, signed, date }) => (
                    <div key={label} className="flex items-center justify-between">
                      <span className="text-[14px] text-[#0e0e0e]">{label}</span>
                      {signed ? (
                        <span className="flex items-center gap-1.5 text-[13px] text-[#166534]">
                          <CheckCircle className="w-4 h-4" />
                          Acknowledged{date && ` on ${new Date(date).toLocaleDateString()}`}
                        </span>
                      ) : (
                        <span className="text-[13px] text-[#a0a0a0]">Pending</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Sign button */}
              {!hasUserSigned() && agreement.status !== 'active' && (
                user?.role === 'business' && !agreement.creative_signed ? (
                  <div className="p-4 rounded-[12px] bg-[#f7f5f2] border border-[#e4e1db] text-center">
                    <p className="text-[13px] text-[#a0a0a0]">Waiting for the photographer to acknowledge the agreement first.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <button
                      onClick={handleSign}
                      disabled={signing}
                      data-testid="sign-agreement-btn"
                      className="w-full py-3 rounded-full bg-[#0e0e0e] text-white text-[14px] font-medium hover:opacity-85 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      <Edit className="w-4 h-4" />
                      {signing ? 'Acknowledging...' : 'Acknowledge Agreement'}
                    </button>
                    <p className="text-center text-[11px] text-[#a0a0a0]">By acknowledging, you agree to FrameFinder's standard terms and conditions.</p>
                  </div>
                )
              )}

              {/* Payment section */}
              {isBothSigned() && booking?.payment_status === 'unpaid' && user?.role === 'business' && (() => {
                const bookingAmount = parseFloat(agreement.total_amount || 0);
                return (
                  <div className="space-y-3">
                    <div className="bg-[#f7f5f2] rounded-[14px] border border-[#e4e1db] p-4 space-y-2 text-[14px]">
                      <div className="flex justify-between text-[#5a5a5a]">
                        <span>Booking amount</span>
                        <span>${bookingAmount.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-[#5a5a5a]">
                        <span className="line-through">Platform fee (15%)</span>
                        <span className="text-right">
                          <span className="line-through text-[#a0a0a0]">${(bookingAmount * 0.15).toFixed(2)}</span>
                          <br />
                          <span className="text-[#16a34a] font-medium text-[12px]">Free Beta</span>
                        </span>
                      </div>
                      <div className="flex justify-between font-semibold text-[#0e0e0e] border-t border-[#e4e1db] pt-2">
                        <span>Total due</span>
                        <span>${bookingAmount.toFixed(2)}</span>
                      </div>
                    </div>
                    <button
                      onClick={handlePayment}
                      data-testid="proceed-payment-btn"
                      className="w-full py-3 rounded-full bg-[#0e0e0e] text-white text-[14px] font-medium hover:opacity-85 transition-opacity flex items-center justify-center gap-2"
                    >
                      <DollarSign className="w-4 h-4" />
                      Proceed to Payment (${bookingAmount.toFixed(2)})
                    </button>
                  </div>
                );
              })()}

              {/* Payment held confirmation */}
              {booking?.payment_status === 'held' && (
                <div className="p-4 rounded-[12px] bg-[#f0fdf4] border border-[#bbf7d0]">
                  <div className="flex items-center gap-2 text-[#166534] text-[14px]">
                    <CheckCircle className="w-4 h-4" />
                    <span className="font-medium">Payment received and held in escrow</span>
                  </div>
                </div>
              )}

              {/* Go to deliverables */}
              {booking?.status === 'in_progress' && (
                <Link to={`/deliverables/${bookingId}`}>
                  <button className="w-full py-3 rounded-full border border-[#e4e1db] bg-white text-[#0e0e0e] text-[14px] font-medium hover:border-[#0e0e0e] transition-colors">
                    Go to Deliverables →
                  </button>
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Agreement;
